import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import SendInterviewDialog from "./SendInterviewDialog.jsx";

/**
 * InterviewsPanel — every interview for this client, and what to do next
 * with each (Plan D).
 *
 * One row per interview: who is answering, where it stands, and the actions
 * that make sense for that state. "Run now" is the live-in-the-room path
 * that always existed; "Send" is the new one. Feeding is unchanged.
 */

function fmtDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" }); }
  catch { return ""; }
}

export function statusLine(iv) {
  const p = iv.progress || { answered: 0, total: 0 };
  switch (iv.status) {
    case "draft":    return "Draft · not sent";
    case "invited":  return `Sent ${fmtDate(iv.invited_at)} · not started`;
    case "active":   return p.total ? `In progress · ${p.answered} of ${p.total}` : "In progress";
    case "complete": return "Complete";
    case "partial":  return "Stopped early";
    case "fed":      return "Fed";
    default:         return iv.status;
  }
}

export default function InterviewsPanel({ clientId, clientName }) {
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);
  const [sending, setSending] = useState(null);   // interview being sent
  const [copied, setCopied] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await fetch(`/api/clients/${clientId}/interviews`);
      if (!r.ok) throw new Error("Couldn't load interviews.");
      setRows(await r.json());
    } catch (e) { setError(e.message); }
  }, [clientId]);
  useEffect(() => { load(); }, [load]);

  function copyLink(iv) {
    const path = iv.link?.url || iv.invitation?.url || `/clients/${clientId}/interviews/${iv.id}/session`;
    try { navigator.clipboard?.writeText(`${window.location.origin}${path}`); } catch { /* */ }
    setCopied(iv.id); setTimeout(() => setCopied(null), 2000);
  }

  async function unsend(iv) {
    if (!window.confirm("Take this back? The link stops working and the interview returns to draft.")) return;
    const r = await fetch(`/api/clients/${clientId}/interviews/${iv.id}/send`, { method: "DELETE" });
    if (!r.ok) { const d = await r.json().catch(() => ({})); setError(d.error || "Couldn't unsend."); return; }
    load();
  }

  async function remove(iv) {
    if (!window.confirm("Delete this interview and its answers?")) return;
    const r = await fetch(`/api/clients/${clientId}/interviews/${iv.id}`, { method: "DELETE" });
    if (!r.ok) { setError("Couldn't delete."); return; }
    load();
  }

  const setupHref   = iv => `/coordinator/clients/${clientId}/interviews/${iv.id}/setup`;
  const sessionHref = iv => `/coordinator/clients/${clientId}/interviews/${iv.id}/session`;

  return (
    <div style={s.body}>
      <div style={s.top}>
        <p style={s.sub}>Ask the people who know {clientName || "them"}.</p>
        <Link to={`/coordinator/clients/${clientId}/interviews/new`} style={s.newBtn}>+ New interview</Link>
      </div>
      {error && <p style={s.error}>{error}</p>}
      {rows === null && !error && <p style={s.muted}>Loading…</p>}
      {rows && rows.length === 0 && <p style={s.muted}>No interviews yet.</p>}
      {rows && rows.length > 0 && (
        <div style={s.list}>
          {rows.map(iv => {
            // Role first, then the person — "Caregiver · Dawn", or the role
            // alone with "not named yet" when nobody has been picked (E3).
            const roleWord = iv.respondent?.role
              ? iv.respondent.role.charAt(0).toUpperCase() + iv.respondent.role.slice(1) : "";
            const who = iv.respondent_name
              ? (roleWord ? `${roleWord} · ${iv.respondent_name}` : iv.respondent_name)
              : (roleWord ? `${roleWord} · not named yet` : "Nobody yet");
            // Send is open until someone has actually answered something —
            // an interview opened in the room but left untouched can still go out.
            const canSend   = iv.status === "draft" || iv.status === "invited"
                           || (iv.status === "active" && iv.has_answers === false);
            const canReview = ["complete", "partial", "active"].includes(iv.status);
            return (
              <div key={iv.id} style={s.row}>
                <div style={s.rowMain}>
                  <span style={s.who}>{who}</span>
                  <span style={s.status}>{statusLine(iv)}</span>
                </div>
                <div style={s.actions}>
                  {iv.status === "draft" && <Link to={setupHref(iv)} style={s.chip}>Set up</Link>}
                  {iv.status === "draft" && <Link to={sessionHref(iv)} style={s.chip}>Run now</Link>}
                  {canSend && <button type="button" style={{ ...s.chip, ...s.chipAccent }} onClick={() => setSending(iv)}>
                    {iv.status === "invited" ? "Resend" : "Send"}
                  </button>}
                  {(iv.status === "invited" || iv.status === "active") && (
                    <button type="button" style={s.chip} onClick={() => copyLink(iv)}>{copied === iv.id ? "Copied" : "Copy link"}</button>
                  )}
                  {iv.status === "invited" && <button type="button" style={s.chip} onClick={() => unsend(iv)}>Unsend</button>}
                  {canReview && <Link to={sessionHref(iv)} style={s.chip}>Review →</Link>}
                  <button type="button" style={{ ...s.chip, ...s.chipIcon }} onClick={() => remove(iv)} aria-label="Delete interview" title="Delete">×</button>
                </div>
              </div>
            );
          })}
        </div>
      )}
      {sending && (
        <SendInterviewDialog clientId={clientId} clientName={clientName} interview={sending}
                             onClose={() => setSending(null)} onSent={() => load()} />
      )}
    </div>
  );
}

const s = {
  body:      { display: "flex", flexDirection: "column", gap: "var(--space-3)" },
  top:       { display: "flex", alignItems: "center", justifyContent: "space-between", gap: "var(--space-3)", flexWrap: "wrap" },
  sub:       { fontSize: "var(--text-sm)", color: "var(--color-text-secondary)", margin: 0 },
  newBtn:    { fontSize: "var(--text-sm)", color: "var(--color-accent)", background: "var(--color-accent-subtle)", borderRadius: "var(--radius-md)", padding: "6px 12px", textDecoration: "none", whiteSpace: "nowrap" },
  muted:     { fontSize: "var(--text-sm)", color: "var(--color-text-tertiary)", margin: 0 },
  error:     { fontSize: "var(--text-sm)", color: "var(--color-critical)", margin: 0 },
  list:      { display: "flex", flexDirection: "column", gap: 4 },
  row:       { display: "flex", alignItems: "center", gap: "var(--space-3)", padding: "var(--space-2) var(--space-3)", borderRadius: "var(--radius-md)", background: "var(--color-bg-base)", flexWrap: "wrap" },
  rowMain:   { flex: 1, minWidth: 160, display: "flex", flexDirection: "column", gap: 2 },
  who:       { fontSize: "var(--text-sm)", fontWeight: "var(--weight-medium)", color: "var(--color-text-primary)" },
  status:    { fontSize: "var(--text-xs)", color: "var(--color-text-tertiary)" },
  actions:   { display: "flex", gap: 4, flexWrap: "wrap" },
  chip:      { font: "inherit", fontSize: "var(--text-xs)", height: 28, padding: "0 10px", display: "inline-flex", alignItems: "center", border: "1px solid var(--color-border-strong)", borderRadius: "var(--radius-md)", background: "var(--color-surface)", color: "var(--color-text-secondary)", textDecoration: "none", cursor: "pointer", whiteSpace: "nowrap" },
  chipAccent:{ color: "var(--color-accent)", borderColor: "var(--color-accent)" },
  chipIcon:  { width: 28, padding: 0, justifyContent: "center", fontSize: 16 },
};
